<script setup lang="ts">
import { ROOT_URL_HOST } from '@/shared/constants/contactConstants';
import AppBreadcrumbs from '@/shared/ui/AppBreadcrumbs/index';
import ChiefEditorCard from '@/widgets/ChiefEditorCard/index';
import EditorAboutSection from '@/pages/editorDetail/ui/EditorAboutSection.vue';
import EditorExperienceSection from '@/pages/editorDetail/ui/EditorExperienceSection.vue';
import EditorPublicationsSection from '@/pages/editorDetail/ui/EditorPublicationsSection.vue';
import EditorArticlesSection from '@/pages/editorDetail/ui/EditorArticlesSection.vue';
import EditorDetailSkeleton from '@/pages/editorDetail/ui/Skeleton/index.vue';
import { useEditorDetail } from '@/pages/editorDetail/composables/useEditorDetail';
import { getErrorStatus } from '@/shared/utils/getErrorStatus';

const EDITOR_PAGE_TITLE = 'Игорь Лесовских — главный редактор журнала Юрик.ру';
const EDITOR_PAGE_DESCRIPTION =
  'Игорь Лесовских — главный редактор Юрик.ру. Опыт, публикации и статьи в журнале о праве: понятные материалы для читателей от практикующего специалиста.';

const isMobile: boolean = useIsMobile();
const route = useRoute();

const {
  chiefEditor,
  isChiefEditorLoading,
  articles,
  publications,
  isShowMoreArticles,
  breadcrumbs,
  loadMoreArticles,
  pageReady,
  pageError,
} = useEditorDetail();

if (import.meta.server) {
  await pageReady;
  if (pageError.value) {
    throw createError({ statusCode: getErrorStatus(pageError.value), fatal: true });
  }
}

const url = computed(() => `${ROOT_URL_HOST}${route.path}`);

useSeoMeta({
  title: EDITOR_PAGE_TITLE,
  description: EDITOR_PAGE_DESCRIPTION,
  ogTitle: EDITOR_PAGE_TITLE,
  ogDescription: EDITOR_PAGE_DESCRIPTION,
  ogSiteName: 'Сервис по подбору юристов, адвокатов - Юрик.ру',
  ogType: 'website',
  ogUrl: url,
  ogLocale: 'ru',
  ogImage: `${ROOT_URL_HOST}/og-image.png`,
});

useHead({
  meta: [{ property: 'vk:image', content: `${ROOT_URL_HOST}/og-image.png` }],
});
</script>

<template>
  <div :class="['editor-detail-page', { _mobile: isMobile }]">
    <EditorDetailSkeleton v-if="isChiefEditorLoading" />
    <div v-else class="editor-detail-page__content">
      <div class="editor-detail-page__breadcrumbs">
        <AppBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <div class="editor-detail-page__card-wrapper">
        <ChiefEditorCard :data="chiefEditor" is-seo-title is-editor-detail-page />
      </div>
      <div class="editor-detail-page__about-wrapper">
        <EditorAboutSection :data="chiefEditor.about" />
      </div>
      <div class="editor-detail-page__experience-wrapper">
        <EditorExperienceSection :data="chiefEditor.experience" />
      </div>
      <div v-if="publications.length" class="editor-detail-page__publications-wrapper">
        <EditorPublicationsSection :data="publications" />
      </div>
      <div v-if="articles.length" class="editor-detail-page__articles-wrapper">
        <EditorArticlesSection
          :articles="articles"
          :is-show-more="isShowMoreArticles"
          @load="loadMoreArticles"
        />
      </div>
    </div>
  </div>
</template>

<style lang="less">
.editor-detail-page {
  .container-d();

  padding-bottom: 100px;

  &__content {
    .flex-column(@size-x10);

    max-width: 880px;
  }

  &__breadcrumbs {
    margin-top: @size-x10;
  }

  &._mobile {
    .container-m();

    padding-bottom: 80px;
  }

  &._mobile &__content {
    max-width: 100%;
  }
}
</style>
